import { useState } from "react";
import {
  List,
  Paper,
  Grid,
  Typography,
  Box,
  styled,
  Button,
  useTheme,
} from "@mui/material";

// Styled components - moved outside component to avoid recreation on each render
const CommentContainer = styled(Paper)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : theme.palette.grey[50],
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  width: "100%",
}));

const LongComment = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "12px",
  lineHeight: 1.5,
  maxHeight: 120,
  overflowY: "auto",
}));

const CommentLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "13px",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

const ExpandButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: 0,
  fontSize: "12px",
  textTransform: "none",
  marginTop: theme.spacing(0.5),
  alignSelf: "flex-start",
  minWidth: "auto",
}));

const EmptyComment = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: "italic",
  fontSize: "0.9rem",
}));

const dateStyle = {
  fontSize: "12px",
  fontWeight: 400,
  color: "text.secondary",
};

const SegmentLabor = ({ labor }) => {
  const theme = useTheme();

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  // State to track expanded labor comments
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [expandedComments, setExpandedComments] = useState<
    Record<string, boolean>
  >({});

  const toggleCommentExpansion = (id: string) => {
    setExpandedComments((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Add null/undefined check for labor prop
  if (!labor || !Array.isArray(labor) || labor.length === 0) {
    return (
      <List>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "grey.100",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            No labor entries found
          </Typography>
        </Paper>
      </List>
    );
  }

  return (
    <List>
      {labor.map((entry, idx) => {
        // Add null/undefined checks for entry properties
        if (!entry) return null;

        const entryId = entry.id || entry.identity || `entry-${idx}`;
        const technicianName = entry.technicianName || "Unknown Technician";
        const elapsedHours = entry.elapsedHours || 0;
        const technicianComment = entry.technicianComment || "";

        return (
          <Paper
            key={entryId}
            elevation={0}
            sx={{
              p: 2,
              mb: 1,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "grey.100",
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={6}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {technicianName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  align="right"
                  color="textSecondary"
                  sx={dateStyle}
                >
                  {entry.modifiedDate ? formatDate(entry.modifiedDate) : "N/A"}
                  <Box
                    component="span"
                    fontWeight="bold"
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.text.primary,
                      marginLeft: 1,
                    }}
                  >
                    {typeof elapsedHours === "number"
                      ? elapsedHours.toFixed(2)
                      : "0.00"}
                    Hrs
                  </Box>
                </Typography>
              </Grid>

              <Box
                sx={{
                  mt: 2,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Long Labor Comment (4000 characters) */}
                {technicianComment && (
                  <Box>
                    <CommentLabel>Labor Comment</CommentLabel>
                    <CommentContainer elevation={0}>
                      {technicianComment ? (
                        <>
                          <LongComment
                            sx={{
                              maxHeight: expandedComments[entryId]
                                ? "none"
                                : 40,
                            }}
                          >
                            {technicianComment}
                          </LongComment>
                          <ExpandButton
                            onClick={() => toggleCommentExpansion(entryId)}
                          >
                            {expandedComments[entryId]
                              ? "Show less"
                              : "Show more"}
                          </ExpandButton>
                        </>
                      ) : (
                        <EmptyComment>No labor comment provided</EmptyComment>
                      )}
                    </CommentContainer>
                  </Box>
                )}
              </Box>
            </Grid>
          </Paper>
        );
      })}
    </List>
  );
};

export default SegmentLabor;
